import { Component } from '@theme/component';

/**
 * Clamps its content to a few lines and adds a Read more / Read less toggle.
 *
 * The clamp is applied here rather than in CSS so that a reader without
 * JavaScript sees the whole text, and so the toggle appears only when the text
 * is actually longer than the clamp. Re-measures on resize, since a narrower
 * window can push short copy over the limit.
 */
class KpReadMore extends Component {
  #resizeObserver = new ResizeObserver(() => this.#measure());

  connectedCallback() {
    super.connectedCallback();

    this.text = this.querySelector('[ref="text"]');
    if (!this.text) return;

    this.dataset.clamped = 'true';
    this.#build();
    this.#measure();
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  #build() {
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'kp-read-more__toggle';
    toggle.textContent = this.dataset.more || 'Read more';
    toggle.setAttribute('aria-expanded', 'false');
    toggle.hidden = true;
    toggle.addEventListener('click', () => this.#toggle(toggle));

    this.append(toggle);
    this.toggle = toggle;
  }

  /**
   * Shows the toggle only when the text overflows its clamp.
   */
  #measure() {
    if (!this.text || !this.toggle || this.dataset.expanded === 'true') return;

    const overflows = this.text.scrollHeight - this.text.clientHeight > 2;
    this.toggle.hidden = !overflows;
  }

  /** @param {HTMLButtonElement} toggle */
  #toggle(toggle) {
    const expanded = this.dataset.expanded === 'true';

    this.dataset.expanded = expanded ? 'false' : 'true';
    toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    toggle.textContent = expanded ? this.dataset.more || 'Read more' : this.dataset.less || 'Read less';

    if (expanded) this.#measure();
  }
}

if (!customElements.get('kp-read-more')) {
  customElements.define('kp-read-more', KpReadMore);
}
